<template>
  <div>
    <naj-input
      :type-id="formFieldType.selectbox"
      :label="locale.debtorAssets.assetType"
      :items="assetTypes"
      :value.sync="formModel.assetTypeId"
      item-text="name"
      item-value="id"
      validates="required"
      :disabled="viewMode"
      @change="assetTypeChanged"
    ></naj-input>
    <naj-dynamic-form
      :form-model="formModel"
      :form-fields="formFields"
      :view-mode="loading || viewMode"
    ></naj-dynamic-form>
  </div>
</template>

<script>
import { debtorAssetTypeEnum } from '@/modules/bankruptcy/helpers/enum';
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('BankruptcyModule');

export default {
  inject: ['$validator'],
  props: {
    formModel: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    viewMode: {
      type: Boolean,
      required: true,
      default: () => false,
    },

  },
  data() {
    return { locale: this.local.bankruptcy.requestDetails };
  },
  computed: {
    ...mapState(['assetTypes', 'realEstateTypes']),
    formFields() {
      return {
        [`asset_${debtorAssetTypeEnum.bankAccount}`]: this.bankAccountFormFields,
        [`asset_${debtorAssetTypeEnum.investmentPortfolio}`]: this.investmentPortfolioFormFields,
        [`asset_${debtorAssetTypeEnum.realEstate}`]: this.realEstateFormFields,
        [`asset_${debtorAssetTypeEnum.vehicle}`]: this.vehicleFormFields,
        [`asset_${debtorAssetTypeEnum.transported}`]: this.transportedFormFields,
        [`asset_${debtorAssetTypeEnum.other}`]: this.otherFormFields,
        [`asset_undefined`]: [],
        [`asset_null`]: [],
      }[`asset_${this.formModel.assetTypeId}`];
    },
    bankAccountFormFields() {
      return [
        {
          id: 1,
          typeId: this.formFieldType.text,
          name: 'assetName',
          label: this.locale.debtorAssets.bankName,
          columnSize: 4,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 2,
          typeId: this.formFieldType.text,
          name: 'assetNumber',
          label: this.locale.debtorAssets.iBAN,
          columnSize: 4,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 3,
          typeId: this.formFieldType.text,
          name: 'assetValue',
          label: this.locale.debtorAssets.assetValue,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
          }),
        },
      ];
    },
    investmentPortfolioFormFields() {
      return [
        {
          id: 4,
          typeId: this.formFieldType.text,
          name: 'assetNumber',
          label: this.locale.debtorAssets.portfolioNumber,
          columnSize: 4,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 5,
          typeId: this.formFieldType.text,
          name: 'assetName',
          label: this.locale.debtorAssets.organizationName,
          columnSize: 4,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 6,
          typeId: this.formFieldType.text,
          name: 'assetValue',
          label: this.locale.debtorAssets.assetValue,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
          }),
        },
      ];
    },
    realEstateFormFields() {
      return [
        {
          id: 7,
          typeId: this.formFieldType.selectbox,
          name: 'realEstateTypeId',
          label: this.locale.debtorAssets.realEstateType,
          columnSize: 4,
          properties: JSON.stringify({
            validations: { required: true },
            textName: 'assetName',
            change: 'setTimeout(() => { if (obj.realEstateTypeId === 99) obj.assetName = null; }, 50)',
            dataSource: this.realEstateTypes,
          }),
        },
        {
          id: 8,
          typeId: this.formFieldType.text,
          name: 'assetName',
          label: this.locale.debtorAssets.otherRealEstateType,
          columnSize: 4,
          properties: JSON.stringify({
            validations: { required: true },
            if: 'obj.realEstateTypeId === 99',
          }),
        },
        {
          id: 9,
          typeId: this.formFieldType.number,
          name: 'judgementCode',
          label: this.locale.debtorAssets.judgementCode,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              long: true,
              digits: 10,
            },
          }),
        },
        {
          id: 10,
          typeId: this.formFieldType.date,
          name: 'judgementDate',
          label: this.locale.debtorAssets.judgementDate,
          columnSize: 4,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 11,
          typeId: this.formFieldType.text,
          name: 'assetValue',
          label: this.locale.debtorAssets.assetValue,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
          }),
        },
        {
          id: 12,
          typeId: this.formFieldType.text,
          name: 'assetLocation',
          label: this.locale.debtorAssets.assetLocation,
          columnSize: this.formModel.realEstateTypeId === 99 ? 4 : 8,
          properties: JSON.stringify({ validations: { required: true } }),
        },
      ];
    },
    vehicleFormFields() {
      return [
        {
          id: 13,
          typeId: this.formFieldType.text,
          name: 'assetName',
          label: this.locale.debtorAssets.vehicleType,
          columnSize: 4,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 14,
          typeId: this.formFieldType.number,
          name: 'assetNumber',
          label: this.locale.debtorAssets.purchaseYear,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              hijri_year: null,
            },
          }),
        },
        {
          id: 15,
          typeId: this.formFieldType.text,
          name: 'assetValue',
          label: this.locale.debtorAssets.assetValue,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
          }),
        },
      ];
    },
    transportedFormFields() {
      return [
        {
          id: 16,
          typeId: this.formFieldType.text,
          name: 'assetName',
          label: this.locale.debtorAssets.transportedType,
          columnSize: 4,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 17,
          typeId: this.formFieldType.number,
          name: 'assetNumber',
          label: this.locale.debtorAssets.purchaseYear,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              hijri_year: null,
            },
          }),
        },
        {
          id: 18,
          typeId: this.formFieldType.text,
          name: 'assetValue',
          label: this.locale.debtorAssets.assetValue,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
          }),
        },
      ];
    },
    otherFormFields() {
      return [
        {
          id: 19,
          typeId: this.formFieldType.text,
          name: 'assetName',
          label: this.locale.debtorAssets.assetType,
          columnSize: 6,
          properties: JSON.stringify({ validations: { required: true } }),
        },
        {
          id: 20,
          typeId: this.formFieldType.text,
          name: 'assetValue',
          label: this.locale.debtorAssets.assetValue,
          columnSize: 6,
          properties: JSON.stringify({
            validations: {
              required: true,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
          }),
        },
      ];
    },
  },
  mounted() {
    this.getAssetTypes();
    this.getRealEstateTypes();
  },
  methods: {
    ...mapActions(['getAssetTypes', 'getRealEstateTypes']),
    assetTypeChanged() {
      for (const key in this.formModel) {
        if (Object.hasOwnProperty.call(this.formModel, key) && key !== 'assetTypeId' && key !== 'objectKey')
          this.$set(this.formModel, key, undefined);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
